import { Avatar, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import HistoryIcon from "@mui/icons-material/History";
import DownloadIcon from "@mui/icons-material/Download";
import { formatUtcDateTime } from "../../../utils/dateTime";

const ACTION_META = {
    UPDATE: { label: "Update", color: "#1976d2", bg: "#e3f2fd" },
    INSERT: { label: "Insert", color: "#2e7d32", bg: "#e8f5e9" },
    DELETE: { label: "Delete", color: "#d32f2f", bg: "#ffebee" },
};

// RULE audit entries carry free-form action text (e.g. "create rule",
// "updated state") instead of an UPDATE/INSERT/DELETE key, so fall back to
// matching keywords and still pick up the right ACTION_META color.
const resolveActionMeta = (action) => {
    if (ACTION_META[action]) return ACTION_META[action];
    const text = (action || "").toLowerCase();
    if (text.includes("update")) return { ...ACTION_META.UPDATE, label: action };
    if (text.includes("create") || text.includes("insert")) return { ...ACTION_META.INSERT, label: action };
    if (text.includes("delete")) return { ...ACTION_META.DELETE, label: action };
    return { label: action || "Update", color: "#616161", bg: "#f5f5f5" };
};

const isCreateAction = (action) => {
    if (action === "INSERT") return true;
    const text = (action || "").toLowerCase();
    return text.includes("create") || text.includes("insert");
};

const hasValue = (value) => value !== null && value !== undefined && value !== "";

// On create, every field is "new" so oldValue is always empty - only show
// fields that actually got a value instead of every tracked field.
const visibleChanges = (entry, changes) =>
    isCreateAction(entry.action) ? changes.filter((change) => hasValue(change.newValue)) : changes;

const getInitials = (value) => {
    if (!value) return "?";
    return value.split("@")[0].slice(0, 2).toUpperCase();
};

const escapeCsvValue = (value) => {
    const str = value === null || value === undefined ? "" : String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const DIFF_FIELD_LINE = /^([+-])\s*"([^"]+)"\s*:\s*(.*?),?\s*$/;
const DIFF_NOISE_LINE = /^(diff --git|index |--- |\+\+\+ |@@|new file|deleted file|\\ No newline)/;

const cleanDiffValue = (raw) => {
    let value = raw.trim();
    if (value.endsWith(",")) value = value.slice(0, -1);
    return /^".*"$/.test(value) ? value.slice(1, -1) : value;
};

// Parses a unified diff of pretty-printed JSON (as produced for RULE audit
// entries) into {key, oldValue, newValue} rows so it renders in the same
// field/change table as the other entity types.
const parseJsonDiff = (diffText) => {
    const removals = new Map();
    const additions = new Map();
    const order = [];

    diffText.split("\n").forEach((line) => {
        if (DIFF_NOISE_LINE.test(line)) return;
        const match = line.match(DIFF_FIELD_LINE);
        if (!match) return;
        const [, sign, key, rawValue] = match;
        const bucket = sign === "-" ? removals : additions;
        if (!bucket.has(key)) bucket.set(key, []);
        bucket.get(key).push(cleanDiffValue(rawValue));
        if (!order.includes(key)) order.push(key);
    });

    return order.flatMap((key) => {
        const oldValues = removals.get(key) || [];
        const newValues = additions.get(key) || [];
        const count = Math.max(oldValues.length, newValues.length);
        return Array.from({ length: count }, (_, i) => ({
            key: count > 1 ? `${key}[${i}]` : key,
            oldValue: oldValues[i],
            newValue: newValues[i],
        }));
    });
};

// For a "create" diff string (new-file unified diff), every line is an
// addition representing the new file's content - strip the "+" markers and
// reconstruct/pretty-print the original JSON.
const extractCreateJsonFromDiff = (diffText) => {
    const text = diffText
        .split("\n")
        .filter((line) => line.startsWith("+") && !line.startsWith("+++"))
        .map((line) => line.slice(1))
        .join("\n");
    try {
        return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
        return text;
    }
};

// What was created, as pretty-printed JSON - parsed from the raw diff for
// RULE entries, or rebuilt from the field changes for DB-tracked entities.
const createPayload = (entry) => {
    if (typeof entry.changes === "string") {
        return entry.changes.trim() ? extractCreateJsonFromDiff(entry.changes) : "";
    }
    const obj = {};
    normalizeChanges(entry.changes).forEach((change) => {
        const key = change.key ?? change.field;
        if (key && hasValue(change.newValue)) obj[key] = change.newValue;
    });
    return JSON.stringify(obj, null, 2);
};

// entry.changes may be an array of {key, oldValue, newValue}, a single such
// object, a map of field -> value/{oldValue,newValue}, or a raw diff string.
const normalizeChanges = (changes) => {
    if (Array.isArray(changes)) return changes;
    if (typeof changes === "string") return changes.trim() ? parseJsonDiff(changes) : [];
    if (changes && typeof changes === "object") {
        if ("key" in changes || "field" in changes || "oldValue" in changes || "newValue" in changes) {
            return [changes];
        }
        return Object.entries(changes).map(([key, value]) => {
            if (value && typeof value === "object" && !Array.isArray(value)) {
                return { key, oldValue: value.oldValue, newValue: value.newValue };
            }
            return { key, oldValue: undefined, newValue: value };
        });
    }
    return [];
};

const buildAuditCsv = (sortedEntries, timezone) => {
    const rows = [
        ["#", "Entity Type", "Entity ID", "Action", "Field", "Old Value", "New Value", "Modified By", "Timestamp"],
    ];

    sortedEntries.forEach((entry, index) => {
        const meta = resolveActionMeta(entry.action);
        const number = sortedEntries.length - index;
        const changes = visibleChanges(entry, normalizeChanges(entry.changes));

        (changes.length ? changes : [{}]).forEach((change) => {
            rows.push([
                number,
                entry.entityType,
                entry.entityId,
                meta.label,
                change.key ?? change.field ?? "",
                change.oldValue ?? "",
                change.newValue ?? "",
                entry.modifiedBy,
                formatUtcDateTime(entry.timestamp, timezone),
            ]);
        });
    });

    return rows.map((row) => row.map(escapeCsvValue).join(",")).join("\n");
};

export default function AuditList({ entries = [], loading = false, entityType = "" }) {
    const timezone = useSelector((state) => state.globalData.timezone);
    if (loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 0 }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (!entityType) {
        return (
            <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 0 }}>
                <HistoryIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                <Typography color="text.secondary">Select a Entity type and enter an Entity Id then search to view audit history.</Typography>
            </Stack>
        );
    }

    if (!entries.length) {
        return (
            <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 0 }}>
                <HistoryIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                <Typography color="text.secondary">No audit history found.</Typography>
            </Stack>
        );
    }

    const sortedEntries = [...entries].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );

    const handleDownloadCsv = () => {
        const csvContent = buildAuditCsv(sortedEntries, timezone);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `audit-history-${Date.now()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <Paper
            elevation={1}
            sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            <Stack direction="row" justifyContent="flex-end" sx={{ px: 3, pt: 2 }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon fontSize="small" />}
                    onClick={handleDownloadCsv}
                >
                    Download CSV
                </Button>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
                <Box
                    sx={{
                        position: "relative",
                        pl: 3.5,
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 8,
                            top: 6,
                            bottom: 6,
                            width: "2px",
                            backgroundColor: "#e0e0e0",
                        },
                    }}
                >
                    {sortedEntries.map((entry, index) => {
                        const meta = resolveActionMeta(entry.action);
                        const isLast = index === entries.length - 1;
                        const number = sortedEntries.length - index;
                        const isCreate = isCreateAction(entry.action);
                        const changes = isCreate ? [] : visibleChanges(entry, normalizeChanges(entry.changes));

                        return (
                            <Box
                                key={entry.id ?? index}
                                sx={{
                                    position: "relative",
                                    pb: isLast ? 0 : 3.5,
                                    "&::before": {
                                        content: `"${number}"`,
                                        position: "absolute",
                                        left: -32,
                                        top: 0,
                                        width: 22,
                                        height: 22,
                                        borderRadius: "50%",
                                        border: "3px solid #fff",
                                        boxShadow: `0 0 0 2px ${meta.color}`,
                                        backgroundColor: meta.color,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontSize: 12,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
                                    <Chip
                                        label={meta.label}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: 0.3,
                                            textTransform: "uppercase",
                                            color: meta.color,
                                            backgroundColor: meta.bg,
                                        }}
                                    />
                                    <Typography variant="body1" color="text.secondary">
                                        <Box component="b" sx={{ color: "rgba(0,0,0,0.8)" }}>
                                            {entry.entityType}
                                        </Box>{" "}
                                        · Entity #{entry.entityId}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ ml: "auto", whiteSpace: "nowrap" }}>
                                        {formatUtcDateTime(entry.timestamp, timezone)}
                                    </Typography>
                                </Stack>

                                {isCreate && (
                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            mb: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            backgroundColor: "#fafafa",
                                            border: "1px solid #eee",
                                            fontSize: 12,
                                            fontFamily: "monospace",
                                            color: "rgba(0,0,0,0.75)",
                                            overflowX: "auto",
                                            whiteSpace: "pre",
                                        }}
                                    >
                                        {createPayload(entry)}
                                    </Box>
                                )}

                                {changes.length > 0 && (
                                    <Box
                                        component="table"
                                        sx={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            // fontSize: 13,
                                            mb: 1,
                                            "& th": {
                                                textAlign: "left",
                                                fontWeight: 600,
                                                color: "rgba(0,0,0,0.6)",
                                                padding: "6px 10px",
                                                borderBottom: "1px solid #eee",
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                letterSpacing: 0.4,
                                            },
                                            "& td": {
                                                padding: "6px 10px",
                                                borderBottom: "1px solid #f5f5f5",
                                                verticalAlign: "top",
                                            },
                                        }}
                                    >
                                        <Box component="thead">
                                            <Box component="tr">
                                                <Box component="th">Field</Box>
                                                <Box component="th">Change</Box>
                                            </Box>
                                        </Box>
                                        <Box component="tbody">
                                            {changes.map((change, idx) => {
                                                const hasOldValue = change.oldValue !== null && change.oldValue !== undefined && change.oldValue !== "";

                                                return (
                                                    <Box component="tr" key={idx}>
                                                        <Box component="td" sx={{ width: "30%", color: "rgba(0,0,0,0.8)" }}>
                                                            {change.key ?? change.field}
                                                        </Box>
                                                        <Box component="td">
                                                            <Box
                                                                component="span"
                                                                sx={
                                                                    hasOldValue
                                                                        ? { color: "#d32f2f", textDecoration: "line-through", textDecorationColor: "rgba(211,47,47,0.4)" }
                                                                        : { color: "rgba(0,0,0,0.35)", fontStyle: "italic" }
                                                                }
                                                            >
                                                                {hasOldValue ? change.oldValue : "—"}
                                                            </Box>
                                                            <Box component="span" sx={{ mx: 0.75, color: "rgba(0,0,0,0.3)" }}>
                                                                →
                                                            </Box>
                                                            <Box component="span" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                                                                {change.newValue ?? "—"}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}

                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Avatar sx={{ width: 22, height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#1976d2" }}>
                                        {getInitials(entry.modifiedBy)}
                                    </Avatar>
                                    <Typography variant="caption" color="text.secondary">
                                        {isCreate ? "Created by" : "Modified by"} {entry.modifiedBy}
                                    </Typography>
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Paper>
    );
}
